// I video delle storie dal lato di mamma (richiesta #25): il lettore, la carta di un video
// (pronto, in preparazione, non riuscito), i gesti sul posto (elimina, riprendi, didascalia),
// la carta del caricamento e quella della ripresa, la riga «I tuoi video» sopra le foto.
//
// Gli indirizzi li compone SEMPRE il server (services/video.ts): qui non si scrive mai un
// /api/media/<id>. E un video non parte mai da solo: niente autoplay, niente muto, niente giro.
import { api, ApiError } from './api.js';
import { EVENTO_VOCE } from './recorder.js';
import { stopSpeaking } from './tts.js';
import { el } from './ui.js';
import { FASI_FINITE, TESTI, caricatore, daRiprendere, lasciaStare, type Regole, type StatoCaricamento } from './video-upload.js';

export type StatoVideo = 'in_coda' | 'in_corso' | 'pronto' | 'fallito';
export type VideoInfo = {
  id: number;
  stato: StatoVideo;
  percento: number | null;
  durataS: number | null;
  larghezza: number | null;
  altezza: number | null;
  didascalia: string | null;
  src: string | null;
  locandina: string | null;
};
/** Una riga `media` di `GET /api/memories/:id` (i video portano `video`, gli altri no). */
export type RigaMedia = {
  id: number;
  kind: string;
  caption: string | null;
  duration_s?: number | null;
  durataS?: number | null;
  /** La foto raccontata che sta in un'altra storia: è in cima alla pagina ma non è di questa (le
   *  azioni sulla storia — posto, «Toglila» — non valgono per lei). */
  altraStoria?: boolean;
  video?: { stato: StatoVideo; larghezza: number | null; altezza: number | null; percento: number | null; src: string | null; locandina: string | null };
};
/** Un video su una pagina dell'album (solo quelli pronti). */
export type VideoPagina = {
  id: number; didascalia: string | null; durataS: number | null;
  larghezza: number | null; altezza: number | null; src: string; locandina: string;
};
/** Una voce di `GET /api/video`. */
export type VideoElenco = {
  id: number; memoryId: number; titolo: string; didascalia: string | null; durataS: number | null;
  miniatura: string | null;
};

/** I video di una storia, nell'ordine in cui lei li vede. */
export function videoDellaStoria(media: RigaMedia[]): VideoInfo[] {
  return media.flatMap((m) => (m.kind === 'video' && m.video
    ? [{
        id: m.id, stato: m.video.stato, percento: m.video.percento, durataS: m.durataS ?? m.duration_s ?? null,
        larghezza: m.video.larghezza, altezza: m.video.altezza, didascalia: m.caption,
        src: m.video.src, locandina: m.video.locandina,
      }]
    : []));
}

/** «1:20». */
export function fmtDurata(secondi: number | null): string {
  if (secondi === null || !Number.isFinite(secondi)) return '';
  const s = Math.max(0, Math.round(secondi));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Mette in pausa ogni video (la voce che legge la storia, una registrazione che comincia). */
export function pausaVideo(): void {
  for (const v of document.querySelectorAll('video')) if (!v.paused) v.pause();
}

/** Prima di cambiare schermata: ogni video si ferma e lascia andare il suo file, così Safari
 *  non continua a scaricare (o a suonare) una pagina che non c'è più. */
export function fermaVideo(): void {
  for (const v of document.querySelectorAll('video')) {
    v.pause();
    if (v.hasAttribute('src')) {
      v.removeAttribute('src');
      v.load();
    }
  }
}

// Una sessione di voce che comincia (anche «Correggi a voce», senza cambiare pagina) ferma i video.
if (typeof window !== 'undefined') window.addEventListener(EVENTO_VOCE, pausaVideo);

/** Il lettore: comandi del sistema, mai autoplay. Le misure vengono dalla copia, così la pagina
 *  non salta quando la locandina arriva. */
export function lettoreVideo(v: { src: string | null; locandina: string | null; larghezza: number | null; altezza: number | null; didascalia: string | null }, titolo: string): HTMLVideoElement {
  const video = el('video', {
    controls: '', playsinline: '', preload: 'none', 'data-ruolo': 'video',
    ...(v.locandina ? { poster: v.locandina } : {}),
    ...(v.src ? { src: v.src } : {}),
    'aria-label': `Video: ${v.didascalia ?? titolo}`,
  });
  if (v.larghezza && v.altezza) video.style.aspectRatio = `${v.larghezza} / ${v.altezza}`;
  video.addEventListener('play', () => {
    // Un video che parte zittisce la voce che leggeva, e gli altri video.
    stopSpeaking();
    for (const altro of document.querySelectorAll('video')) if (altro !== video && !altro.paused) altro.pause();
  });
  return video;
}

/** Un video nella pagina dell'album, dopo le fotografie: niente angolini né inclinazioni. */
export function figuraVideo(v: VideoPagina, titolo: string): HTMLElement {
  return el('figure', { class: 'album-video', 'data-video-id': String(v.id) },
    lettoreVideo(v, titolo),
    v.didascalia ? el('figcaption', {}, v.didascalia) : null,
  );
}

/** Dalla riga «I tuoi video» si arriva alla storia, all'altezza del video (mai in riproduzione).
 *  Le fotografie sopra arrivano dopo e lo spingono giù: si insiste un attimo, poi basta, e
 *  appena lei scorre da sé ci si ferma. */
export function scorriAlVideo(id: number): void {
  const vai = (): void => {
    document.querySelector(`[data-video-id="${id}"]`)?.scrollIntoView({ block: 'center' });
    ultimo = window.scrollY;
  };
  let ultimo = window.scrollY;
  vai();
  for (const ms of [250, 700, 1400]) {
    window.setTimeout(() => { if (Math.abs(window.scrollY - ultimo) <= 2) vai(); }, ms);
  }
}

type CtxCarta = { caro: () => string; titolo: string; onSparito?: () => void };

/** Un video della storia: pronto da guardare, in preparazione (si riguarda da solo ogni dieci
 *  secondi finché resta sullo schermo) o non riuscito. Elimina e didascalia si fanno sul posto.
 *  Il riguardare cambia SOLO il corpo della carta: i pulsanti, e la didascalia che lei sta
 *  scrivendo intanto («Lì puoi anche scrivere una didascalia»), restano come sono. */
export function cartaVideo(iniziale: VideoInfo, ctx: CtxCarta): HTMLElement {
  let v = iniziale;
  let giro = 0;
  let lettore: HTMLVideoElement | null = null;
  /** Il corpo di adesso: il lettore, l'attesa o il «non riuscito». */
  let figura: HTMLElement | null = null;
  const carta = el('div', { class: 'video-carta', 'data-video-id': String(v.id) });
  const esito = el('p', { class: 'video-esito', role: 'status' });
  const azioni = el('div', { class: 'video-azioni' });

  function corpo(): HTMLElement {
    if (v.stato === 'pronto' && v.src) {
      // Lo stesso file: lo stesso lettore (una didascalia nuova non deve fermare il video).
      if (!lettore || lettore.getAttribute('src') !== v.src) lettore = lettoreVideo(v, ctx.titolo);
      else lettore.setAttribute('aria-label', `Video: ${v.didascalia ?? ctx.titolo}`);
      return el('figure', { class: 'video-figura' },
        lettore,
        v.didascalia || v.durataS
          ? el('figcaption', {}, v.didascalia ?? '', v.durataS ? el('span', { class: 'video-durata' }, `${v.didascalia ? ' · ' : ''}${fmtDurata(v.durataS)}`) : null)
          : null,
      );
    }
    lettore = null;
    if (v.stato === 'fallito') {
      return el('p', { class: 'video-attesa video-fallito' },
        `Questo video non sono riuscita a prepararlo. È al sicuro, e ${ctx.caro()} lo sa.`);
    }
    const p = v.stato === 'in_corso' && v.percento !== null && v.percento > 0 ? ` Sono al ${v.percento}%.` : '';
    return el('p', { class: 'video-attesa', role: 'status' },
      el('span', { 'aria-hidden': 'true' }, '🎞 '),
      `Sto preparando il video… Lo trovi qui quando è pronto.${p}`,
      v.didascalia ? el('span', { class: 'video-durata' }, ` «${v.didascalia}»`) : null,
    );
  }

  function mostraAzioni(): void {
    azioni.replaceChildren(
      el('button', { class: 'btn-quieto', 'data-ruolo': 'didascalia-video', click: apriDidascalia }, '✏️  Scrivi una didascalia'),
      el('button', { class: 'btn-quieto', 'data-ruolo': 'elimina-video', click: elimina }, '🗑  Elimina il video'),
    );
  }

  /** Tutta la carta da capo: all'inizio, dopo «Riprendilo» e dopo una didascalia salvata. */
  function disegna(nuovo: VideoInfo): void {
    v = nuovo;
    figura = corpo();
    carta.replaceChildren(figura, azioni, esito);
    mostraAzioni();
    pianifica();
  }

  /** Solo il corpo (lo stato, la percentuale, il lettore quando è pronto): niente altro si tocca. */
  function aggiornaCorpo(nuovo: VideoInfo): void {
    v = nuovo;
    if (!figura || figura.parentNode !== carta) return;
    const nuova = corpo();
    figura.replaceWith(nuova);
    figura = nuova;
    pianifica();
  }

  function pianifica(): void {
    const mio = ++giro;
    if (v.stato === 'in_coda' || v.stato === 'in_corso') {
      window.setTimeout(() => void riguarda(mio), 10_000);
    }
  }

  async function riguarda(mio: number): Promise<void> {
    if (mio !== giro || !carta.isConnected) return;
    try {
      const n = await api.get<VideoInfo>(`/api/video/${v.id}`);
      if (mio !== giro || !carta.isConnected) return;
      if (n.stato !== v.stato || n.percento !== v.percento) aggiornaCorpo(n);
      else window.setTimeout(() => void riguarda(mio), 10_000);
    } catch (err) {
      if (mio !== giro || !carta.isConnected) return;
      // Eliminato da un'altra parte (il quaderno, un altro tablet): la storia si riallinea.
      if (err instanceof ApiError && err.status === 404) { ctx.onSparito?.(); return; }
      window.setTimeout(() => void riguarda(mio), 10_000);
    }
  }

  // Niente domanda prima: il tocco fa subito la cosa, e al suo posto compare «Riprendilo».
  async function elimina(): Promise<void> {
    esito.textContent = '';
    try {
      await api.post(`/api/video/${v.id}/nascondi`);
    } catch {
      esito.textContent = 'Non sono riuscita a toglierlo: riprova.';
      return;
    }
    giro++;
    lettore?.pause();
    lettore = null;
    const riprendi = el('button', { class: 'btn-verde', 'data-ruolo': 'riprendi-video', click: riprendiVideo }, '↩️  Riprendilo');
    carta.replaceChildren(
      el('p', { class: 'video-tolto', role: 'status' }, 'Video tolto ✓ Se ci hai ripensato, riprendilo qui sotto.'),
      riprendi,
      esito,
    );
    riprendi.focus();
  }

  async function riprendiVideo(): Promise<void> {
    esito.textContent = '';
    try {
      await api.post(`/api/video/${v.id}/riprendi`);
      disegna(await api.get<VideoInfo>(`/api/video/${v.id}`));
      esito.textContent = 'Eccolo di nuovo qui ✓';
      (carta.querySelector<HTMLElement>('video') ?? carta.querySelector<HTMLElement>('[data-ruolo="elimina-video"]'))?.focus();
    } catch {
      esito.textContent = `Non sono riuscita a riprenderlo: chiedi a ${ctx.caro()}.`;
    }
  }

  function apriDidascalia(): void {
    esito.textContent = '';
    const inp = el('input', {
      class: 'scrivi-testo', value: v.didascalia ?? '', placeholder: 'Scrivi la didascalia…',
      'aria-label': 'Didascalia del video', maxlength: '500',
    }) as HTMLInputElement;
    const salva = el('button', { class: 'btn-verde', 'data-ruolo': 'salva-didascalia', click: async () => {
      try {
        const r = await api.put<{ didascalia: string | null }>(`/api/video/${v.id}/didascalia`, { didascalia: inp.value });
        disegna({ ...v, didascalia: r.didascalia });
        esito.textContent = r.didascalia ? 'Didascalia salvata ✓' : 'Didascalia tolta ✓';
        carta.querySelector<HTMLElement>('[data-ruolo="didascalia-video"]')?.focus();
      } catch {
        esito.textContent = 'Non riesco a salvare la didascalia: riprova.';
      }
    } }, 'Salva');
    azioni.replaceChildren(
      el('div', { class: 'scrivi-area' }, inp, salva),
      el('button', { class: 'btn-quieto', click: () => { mostraAzioni(); carta.querySelector<HTMLElement>('[data-ruolo="didascalia-video"]')?.focus(); } }, 'Annulla'),
    );
    inp.focus();
  }

  disegna(v);
  return carta;
}

type CtxPannello = {
  caro: () => string;
  regole: Regole | null;
  /** Apre la scelta del file («Sceglilo di nuovo»). */
  scegli: () => void;
  /** Il video è arrivato (o è tornato): la storia si riallinea. */
  onArrivato: () => void;
  onCambio: () => void;
};

/** La carta del caricamento: la barra grande, le frasi, «Annulla»; e, se il quaderno si era
 *  chiuso a metà, la carta per riprenderlo. Si aggancia al caricatore, che vive fuori dalle
 *  schermate: tornando sulla storia, la barra riprende da dove è. */
export function pannelloCaricamento(memoryId: number, ctx: CtxPannello): { element: HTMLElement; avviso: (testo: string) => void } {
  const testo = el('p', { class: 'video-carica-testo', role: 'status' });
  const barra = el('progress', { class: 'video-carica-barra', max: '100', value: '0', 'aria-label': 'Quanto del video è arrivato nel quaderno' }) as HTMLProgressElement;
  const nota = el('p', { class: 'video-carica-nota' }, TESTI.tieniAperto);
  const scelte = el('div', { class: 'video-carica-scelte' });
  const annulla = el('button', { class: 'btn-quieto', 'data-ruolo': 'annulla-video', click: () => caricatore.annulla() }, 'Annulla');
  const element = el('div', { class: 'carta video-carica', 'data-ruolo': 'video-carica', hidden: '' }, testo, barra, nota, scelte, annulla);

  const metti = (o: { testo: string; barra?: number | null; nota?: boolean; annulla?: boolean; scelte?: HTMLElement[] }): void => {
    element.hidden = false;
    testo.textContent = o.testo;
    barra.hidden = o.barra === undefined || o.barra === null;
    if (!barra.hidden) barra.value = o.barra!;
    nota.hidden = !o.nota;
    annulla.hidden = !o.annulla;
    scelte.replaceChildren(...(o.scelte ?? []));
    ctx.onCambio();
  };

  let ultimaFase: StatoCaricamento['fase'] | null = null;
  const mostra = (s: StatoCaricamento): void => {
    const finita = FASI_FINITE.has(s.fase);
    metti({
      testo: s.testo,
      barra: finita ? null : s.percento,
      nota: s.fase === 'invio' || s.fase === 'voce' || s.fase === 'rete',
      annulla: !finita && s.fase !== 'unione',
    });
    if (finita) caricatore.segnaVista();
    if ((s.fase === 'arrivato' || s.fase === 'gia') && ultimaFase !== s.fase) ctx.onArrivato();
    ultimaFase = s.fase;
  };

  // Il caricatore vive più a lungo della schermata: quando la carta non c'è più, si stacca.
  let montato = false;
  const via = caricatore.ascolta((s) => {
    if (!element.isConnected) {
      if (montato) via();
      return;
    }
    if (s.memoryId === memoryId) mostra(s);
  });
  queueMicrotask(() => { montato = element.isConnected; });
  window.setTimeout(() => { if (!element.isConnected) via(); }, 5000);

  // Una fase finita già vista non si ripete; una ancora da vedere sì (è arrivato mentre lei era altrove).
  caricatore.dimenticaVista(memoryId);
  const s = caricatore.statoPer(memoryId);
  if (s) {
    ultimaFase = s.fase;
    mostra(s);
  } else {
    void (async () => {
      const r = await daRiprendere(memoryId);
      if (!r || !element.isConnected || caricatore.statoPer(memoryId)) return;
      if (r.tipo === 'arrivato') {
        metti({ testo: TESTI.arrivatoPrima });
        ctx.onArrivato();
      } else if (r.tipo === 'unione') {
        caricatore.chiudiArrivato(r.segno, ctx.caro());
      } else {
        metti({
          testo: TESTI.ripresa,
          scelte: [
            ...(ctx.regole ? [el('button', { class: 'btn-secondario', 'data-ruolo': 'scegli-di-nuovo', click: () => ctx.scegli() }, '🎞  Sceglilo di nuovo')] : []),
            el('button', { class: 'btn-quieto', 'data-ruolo': 'lascia-stare', click: async () => {
              await lasciaStare(r.segno);
              metti({ testo: TESTI.annullato });
            } }, 'Lascia stare'),
          ],
        });
      }
    })();
  }

  return { element, avviso: (t: string) => metti({ testo: t }) };
}

/** La sezione dei video di una storia: le carte, il caricamento e la scelta del file. Il
 *  pulsante «🎞 Aggiungi un video» (in fondo alla storia, accanto a «📷 Aggiungi una foto»)
 *  chiama `scegli()`; il caricamento si vede qui, e la pagina scorre fin qui. */
export function sezioneVideo(
  memoryId: number,
  iniziali: VideoInfo[],
  ctx: { caro: () => string; titolo: string; regole: Regole | null },
): { element: HTMLElement; scegli: () => void; ricarica: () => Promise<void> } {
  const titolo = el('h2', { class: 'sezione video-titolo' });
  const elenco = el('div', { class: 'video-elenco' });
  const input = el('input', { type: 'file', accept: 'video/*', hidden: '', 'data-ruolo': 'scegli-video' }) as HTMLInputElement;
  let quanti = 0;
  const sezione = el('section', { class: 'video-storia', 'data-ruolo': 'video-sezione', 'aria-label': 'I video della storia' });
  // Il pannello nasce dopo (gli serve `scegli`), ma può già parlare mentre nasce.
  let pannello: ReturnType<typeof pannelloCaricamento> | null = null;

  function visibilita(): void {
    titolo.hidden = quanti === 0;
    sezione.hidden = quanti === 0 && (pannello?.element.hidden ?? true);
  }

  function disegna(lista: VideoInfo[]): void {
    quanti = lista.length;
    titolo.textContent = quanti === 1 ? '🎞 Il video' : '🎞 I video';
    elenco.replaceChildren(...lista.map((v) => cartaVideo(v, { caro: ctx.caro, titolo: ctx.titolo, onSparito: () => void ricarica() })));
    visibilita();
  }

  async function ricarica(): Promise<void> {
    try {
      const m = await api.get<{ media: RigaMedia[] }>(`/api/memories/${memoryId}`);
      if (sezione.isConnected) disegna(videoDellaStoria(m.media));
    } catch { /* resta com'è: al prossimo giro si riallinea */ }
  }

  function scegli(): void {
    if (caricatore.attivo()) {
      // Uno alla volta. Se è proprio il video di questa storia, la barra dice già tutto.
      if (!caricatore.statoPer(memoryId)) pannello?.avviso(TESTI.occupato);
      pannello?.element.scrollIntoView({ block: 'center' });
      return;
    }
    input.click();
  }

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    input.value = ''; // così lo stesso video si può scegliere di nuovo
    if (!file || !ctx.regole) return;
    if (caricatore.attivo()) { pannello?.avviso(TESTI.occupato); return; }
    caricatore.avvia(file, memoryId, ctx.regole, ctx.caro());
    pannello?.element.scrollIntoView({ block: 'center' });
  });

  pannello = pannelloCaricamento(memoryId, {
    caro: ctx.caro, regole: ctx.regole, scegli, onArrivato: () => void ricarica(), onCambio: () => visibilita(),
  });
  sezione.append(titolo, elenco, pannello.element, input);
  disegna(iniziali);
  return { element: sezione, scegli, ricarica };
}

/** «🎞 I tuoi video», sopra le fotografie: le locandine piccole, ▶, la durata e il titolo della
 *  storia. Un tocco porta alla storia, all'altezza del video. I video non si mescolano alle foto. */
export function rigaVideo(lista: VideoElenco[]): HTMLElement | null {
  if (lista.length === 0) return null;
  return el('section', { class: 'video-riga', 'data-ruolo': 'video-riga', 'aria-labelledby': 'video-riga-titolo' },
    el('h2', { class: 'sezione', id: 'video-riga-titolo' }, '🎞 I tuoi video'),
    el('div', { class: 'video-riga-elenco' },
      ...lista.map((v) => el('button', {
        class: 'video-riga-voce', 'data-video-id': String(v.id),
        click: () => { location.hash = `#storia/${v.memoryId}?video=${v.id}`; },
      },
        el('span', { class: 'video-riga-locandina' },
          v.miniatura ? el('img', { src: v.miniatura, alt: '', loading: 'lazy', decoding: 'async' }) : null,
          el('span', { class: 'video-riga-play', 'aria-hidden': 'true' }, '▶'),
          v.durataS ? el('span', { class: 'video-riga-durata' }, fmtDurata(v.durataS)) : null,
        ),
        el('span', { class: 'video-riga-titolo' }, v.titolo),
      )),
    ),
  );
}
